import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { type MonoTypeOperatorFunction, type Observable, TimeoutError, catchError, map, of, retry, shareReplay, throwError, timeout, timer } from 'rxjs';

import {
  type PagePermissionMap,
  type PermissionLevel,
  type PortalPage,
  PORTAL_PAGES,
  reaches,
  roleDefaultPermissions,
} from '@models/page-permission.model';
import { type TenantConfig, type TenantFeature } from '@models/tenant-config.model';
import { type UserRole } from '@models/user.model';

import { environment } from '@environments/environment';

/** Outcome of a role check. `unreachable` = the server could not be asked, which is not "no permission". */
export type RoleAccess = 'allowed' | 'denied' | 'unreachable';

/** Outcome of a feature check. `unreachable` = the server could not be asked, which is not "feature off". */
export type FeatureAccess = 'enabled' | 'disabled' | 'unreachable';

interface Me {
  role: UserRole;
  onboardingCompleted: boolean;
  tenantId?: string | null;
  firstName: string | null;
  lastName: string | null;
  pagePermissions?: PagePermissionMap;
}

type MeResult = { status: 'ok'; me: Me } | { status: 'denied' } | { status: 'unreachable' };
type ConfigResult = { status: 'ok'; config: TenantConfig } | { status: 'unreachable' };

/** No connection, timeout, overloaded or broken server: worth a retry, says nothing about the user's rights. */
export function isUnreachable(err: unknown): boolean {
  if (err instanceof TimeoutError) {return true;}
  return err instanceof HttpErrorResponse && (err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500);
}

/** Retry only what may heal by waiting; a 403 or 404 answers the same the second time. */
function retryWhenUnreachable<T>(count: number, delayMs: number): MonoTypeOperatorFunction<T> {
  return retry({ count, delay: (err: unknown) => (isUnreachable(err) ? timer(delayMs) : throwError(() => err)) });
}

/**
 * Provides the role/feature checks the route guards need.
 *
 * Deliberately narrower than Mobile-Client's AppAuthService + TenantConfigService: Portal-SPA
 * only needs the DB-resolved role (never trust the Auth0 JWT role claim) and the tenant's
 * enabled features, both cached with shareReplay(1) for the service's lifetime.
 *
 * A failed request because the server is unreachable is NOT cached (the next check asks again)
 * and is reported as `unreachable`, so guards and pages can say "Verbindung fehlgeschlagen"
 * instead of the false "keine Berechtigung" / "Funktion nicht aktiviert".
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);

  private meRequest$: Observable<MeResult> | null = null;
  private configRequest$: Observable<ConfigResult> | null = null;

  private loadMe(): Observable<MeResult> {
    if (!this.meRequest$) {
      const request$: Observable<MeResult> = this.http.get<Me>(`${environment.apiBaseUrl}/api/profile/me`).pipe(
        // Hard cap + one retry so a slow/hung request or a single cold-start blip can't leave a
        // guard waiting forever, without masking a genuinely failed call as "no role".
        timeout({ first: 12000 }),
        retryWhenUnreachable(1, 2000),
        map((me): MeResult => ({ status: 'ok', me })),
        catchError((err: unknown) => {
          const unreachable = isUnreachable(err);
          if (unreachable && this.meRequest$ === request$) {this.meRequest$ = null;}
          return of<MeResult>({ status: unreachable ? 'unreachable' : 'denied' });
        }),
        shareReplay(1),
      );
      this.meRequest$ = request$;
    }
    return this.meRequest$;
  }

  private loadConfig(): Observable<ConfigResult> {
    if (!this.configRequest$) {
      const request$: Observable<ConfigResult> = this.http.get<TenantConfig>(`${environment.apiBaseUrl}/api/tenant/config`).pipe(
        retryWhenUnreachable(2, 1500),
        map((config): ConfigResult => ({ status: 'ok', config })),
        catchError(() => {
          if (this.configRequest$ === request$) {this.configRequest$ = null;}
          return of<ConfigResult>({ status: 'unreachable' });
        }),
        shareReplay(1),
      );
      this.configRequest$ = request$;
    }
    return this.configRequest$;
  }

  /** Admin always passes every role check (mirrors Mobile-Client's AppAuthService.hasRole). */
  roleAccess(role: UserRole): Observable<RoleAccess> {
    return this.loadMe().pipe(
      map((result): RoleAccess => {
        if (result.status !== 'ok') {return result.status === 'unreachable' ? 'unreachable' : 'denied';}
        return result.me.role === 'admin' || result.me.role === role ? 'allowed' : 'denied';
      }),
    );
  }

  featureAccess(feature: TenantFeature): Observable<FeatureAccess> {
    return this.loadConfig().pipe(
      map((result): FeatureAccess => {
        if (result.status !== 'ok') {return 'unreachable';}
        return result.config.features.includes(feature) ? 'enabled' : 'disabled';
      }),
    );
  }

  hasRole(role: UserRole): Observable<boolean> {
    return this.roleAccess(role).pipe(map((access) => access === 'allowed'));
  }

  /**
   * The user's level on every page. Without a profile (failed load) nothing is allowed; an API that does not
   * send `pagePermissions` yet is read as the default of the role.
   */
  pagePermissions$(): Observable<PagePermissionMap> {
    return this.loadMe().pipe(
      map((result): PagePermissionMap => (result.status === 'ok' ? (result.me.pagePermissions ?? roleDefaultPermissions(result.me.role)) : {})),
    );
  }

  /** True if the user reaches `level` (default read) on the page. */
  hasPage(page: PortalPage, level: PermissionLevel = 'read'): Observable<boolean> {
    return this.pagePermissions$().pipe(map((levels) => reaches(levels[page], level)));
  }

  /** The pages the user may at least read, in navigation order. */
  readablePages$(): Observable<PortalPage[]> {
    return this.pagePermissions$().pipe(map((levels) => PORTAL_PAGES.filter((p) => reaches(levels[p], 'read'))));
  }

  isFeatureEnabled(feature: TenantFeature): Observable<boolean> {
    return this.featureAccess(feature).pipe(map((access) => access === 'enabled'));
  }

  /**
   * The tenant the server actually serves this session from (after an X-Admin-TenantId switch).
   * `undefined` while unknown: the profile failed to load, or an API that predates the field.
   * Callers must not treat that as "no tenant".
   */
  servedTenantId$(): Observable<string | null | undefined> {
    return this.loadMe().pipe(
      map((result) => (result.status === 'ok' && result.me.tenantId !== undefined ? result.me.tenantId : undefined)),
    );
  }

  /** "Vorname Nachname" from the Employees record, or null if unavailable (e.g. a platform
   *  admin with no Employees row) — callers should fall back to the Auth0 email in that case. */
  displayName$(): Observable<string | null> {
    return this.loadMe().pipe(
      map((result) => {
        const me = result.status === 'ok' ? result.me : null;
        const name = [me?.firstName, me?.lastName].filter((part) => !!part).join(' ');
        return name || null;
      }),
    );
  }
}
